/* eslint-disable @typescript-eslint/no-unused-vars */

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMenuLayout } from 'api/hooks/useMenuLayout'
import { useGetCategories } from 'api/hooks/useGetCategories'
import { useSearchParams } from 'react-router-dom'

import { SplashScreen } from 'components/menus/splash-screen'
import { useHiddenProducts } from 'api/hooks/useHiddenProducts'
// import { OurSurprise } from "components/menus/our-surprise";
import { HeaderV2 } from 'components/menus/headerV2'
import { Footer } from 'components/menus/footer'
import { getRGBColor } from 'utils'
import Skeleton from 'react-loading-skeleton'
import { useLocales } from 'api/hooks/useGetLocalesFormSlug'
import { Enum_Menu_Locale } from 'api/graphql/types.generated'
import ReservationButton from 'components/reservationButton'
import { StickyCategoriesV2 } from 'components/menus/sticky-categories-v2'
import { CategoriesV2 } from 'components/menus/categories-v2'

export const Restaurant = () => {
  const [locale, setLocale] = useState(navigator.language)
  const [showLoader, setShowLoader] = useState(true)

  const { data: categories, categoriesIsFetched } = useGetCategories(locale)
  const [searchParams] = useSearchParams()

  const { locales } = useLocales()

  const { layout, layoutIsFetched } = useMenuLayout(locale)
  const { hidden_products } = useHiddenProducts()
  const { i18n } = useTranslation()

  const current = i18n.language

  const handleLocale = (lng: string) => {
    i18n.changeLanguage(lng)
    setLocale(lng)
  }

  useEffect(() => {
    const browserLang = navigator.language.split('-')[0] as Enum_Menu_Locale

    if (locales && locales.includes(browserLang)) {
      const htmlElement = document.documentElement
      htmlElement.setAttribute('translate', 'no')
    }

    if (current !== locale) i18n.changeLanguage(locale)
  }, [locale, i18n, current, locales])

  useEffect(() => {
    // Set locale to "en" if no data !
    if (!layout && layoutIsFetched) {
      setLocale('en')
    }
  }, [layoutIsFetched, layout])

  useEffect(() => {
    if (!searchParams.get('categorie')) {
      const element = document.querySelector('#header')
      if (element) {
        element.scrollIntoView(true)
      }
    }
  }, [searchParams])

  useEffect(() => {
    if (layout) {
      async function loadTitleFonts() {
        const font = new FontFace(
          'titleFont',
          `url(${layout?.title_font?.data?.attributes?.font_file?.data?.attributes?.url})`
        )
        await font.load()
        document.fonts.add(font)
        document.body.classList.add('titleFont-loaded')
      }
      async function loadBodyFonts() {
        const font = new FontFace(
          'bodyFont',
          `url(${layout?.body_font?.data?.attributes?.font_file?.data?.attributes?.url})`
        )
        await font.load()
        document.fonts.add(font)
        document.body.classList.add('bodyFont-loaded')
      }
      async function loadDescriptionFonts() {
        const font = new FontFace(
          'descriptionFont',
          `url(${layout?.description_font?.data?.attributes?.font_file?.data?.attributes?.url})`
        )
        await font.load()
        document.fonts.add(font)
        document.body.classList.add('bodyFont-loaded')
      }
      loadTitleFonts()
      loadDescriptionFonts()
      loadBodyFonts()
    }
  }, [layout])

  useEffect(() => {
    console.log('CATEGORIES', categories)
    const timer = setTimeout(() => {
      setShowLoader(false)
    }, 1000)
    return () => clearTimeout(timer)
    // if (categoriesIsFetched) setShowLoader(false)
  }, [categoriesIsFetched])

  if (!layout) {
    return <></>
  }

  const background_color = layout.background_color ?? '#ffffff'
  const font_color = layout.font_color ?? '#000000'
  const secondary_color = layout.secondary_color ?? '#ffffff  '

  const primary = getRGBColor(font_color, 'primary')
  const secondary = getRGBColor(background_color, 'secondary')
  const tertiary = getRGBColor(secondary_color, 'tertiary')

  const isArabic = locale.startsWith('ar') || locale === 'ar'

  if (showLoader && layout) {
    return (
      <SplashScreen
        restaurantAdress={layout.restaurant_adress}
        color={font_color}
        restaurantName={layout.restaurant}
        imgUrl={layout?.logo?.data?.attributes?.url}
      />
    )
  }

  console.log('RESTAURANT LAYOUT', layout)

  return (
    <>
      <style>:root {`{${primary} ${secondary} ${tertiary}}`}</style>
      <div id="main" className="h-screen overflow-x-hidden font-mammaBody">
        <div className="container flex justify-center w-full max-w-[768px] mx-auto">
          <div className="w-full ">
            <div className="sticky top-0 z-50">
              {/* HEADER */}
              <HeaderV2
                setLocale={(lng) => handleLocale(lng)}
                wifi={layout.wifi_code}
                showCaloriesBtn={layout.show_calories_button}
                showAllergensBtn={layout.show_allergen_button}
                description={layout.description}
                isLoading={!layoutIsFetched}
                sub_description={layout.sub_description}
                restaurantName={layout.restaurant}
                imgUrl={layout?.logo?.data?.attributes?.url}
                // body_font={layout.body_font}
              />

              {/* STICKY CATEGORIES */}
              {categoriesIsFetched && <StickyCategoriesV2 isArabic={isArabic} categories={categories} />}
            </div>

            {layout.description && (
              <div className="mb-[26px] mt-6 font-mammaBody text-primary">
                <p className="font-mammaDescription whitespace-pre-wrap text-center text-[14px] font-semibold leading-[120%]">
                  {layout.description}
                </p>
                <p className="mt-3 text-center text-[14px] font-bold tracking-[0.02em] leading-[120%]">
                  OPEN WIFI: {layout.wifi_code}
                </p>
                {layout?.cover?.data?.attributes?.url && (
                  <img
                    className="max-h-[76px] mx-auto mt-6 max-w-full"
                    src={layout?.cover?.data?.attributes?.url}
                    alt={layout.restaurant || 'restaurant logo'}
                  />
                )}
              </div>
            )}

            {/* CATEGORIES */}
            {showLoader ? (
              <div className="h-auto">
                <SplashScreen
                  restaurantAdress={layout.restaurant_adress}
                  restaurantName={layout.restaurant}
                  color={font_color}
                  imgUrl={layout?.logo?.data?.attributes?.url}
                />
              </div>
            ) : (
              <CategoriesV2 categories={categories} hidden_products={hidden_products} isArabic={isArabic} />
            )}

            <Footer
              image={layout.our_surprise?.data?.attributes?.url}
              surpriseLink={layout.our_surprise_link}
              allergens={layout.footer_allergens}
              teams={layout.footer_team}
            />
          </div>
        </div>
      </div>
      <ReservationButton reservationUrl={layout.reservation_url} label={layout.reservation_button_label} />
    </>
  )
}
